import { useState } from "react";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, PackageBadge, StatusPill } from "@/components/ui-bits";
import { Search, Sparkles, Network, Loader2, Layers } from "lucide-react";
import { MatrixTree } from "@/components/MatrixTree";

export default function AdminMagicCards() {
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (!q.trim()) return toast.error("Enter a User ID or email");
    setBusy(true);
    try {
      const r = await API.get("/admin/magic-cards", { params: { q: q.trim() } });
      setD(r.data);
    } catch (e) { setD(null); toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const m = d?.matrix;
  const root = m ? m.root : null;

  return (
    <div>
      <PageHeader title="Magic Card System" subtitle="Search a member by User ID (referral code) or email to inspect their Magic Card matrix level structure." testid="admin-cards-header" />

      <div className="glass rounded-2xl p-3 mb-6 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="h-4 w-4 text-slate-500" />
          <input data-testid="mc-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            placeholder="Enter User ID (e.g. SO123ABC) or email..." className="w-full bg-transparent text-white outline-none py-2" />
        </div>
        <button data-testid="mc-search-btn" onClick={search} disabled={busy}
          className="inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-60 text-white font-semibold px-5 py-2 rounded-xl">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Search
        </button>
      </div>

      {!d ? (
        <div className="glass rounded-2xl p-12 text-center text-slate-500" data-testid="mc-empty">
          Search a member to view their Magic Card matrix.
        </div>
      ) : (
        <>
          <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="mc-member">
            <div>
              <div className="text-white font-semibold">{d.member.name}</div>
              <div className="text-xs text-slate-500">{d.member.email} · {d.member.mobile}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-num text-cyan-300 text-sm">{d.member.referral_code}</span>
              <PackageBadge id={d.member.package_id} />
              <StatusPill status={d.member.status} />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard testid="mc-total" label="Cards Total" value={d.cards_total} icon={Sparkles} accent="violet" />
            <StatCard testid="mc-used" label="Dropped" value={d.cards_used} icon={Layers} accent="cyan" />
            <StatCard testid="mc-avail" label="Available" value={d.cards_available} icon={Sparkles} accent="emerald" />
            <StatCard testid="mc-drops" label="Matrix Drops" value={m.total_drops} icon={Network} accent="gold" />
          </div>

          <div className="glass rounded-2xl p-6 mb-6">
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-1">
              <Network className="h-5 w-5 text-cyan-400" /> Level Structure (3-wide · {m.level_slots.join(" / ")})
            </h3>
            <p className="text-xs text-slate-500 mb-4">Each dropped card pays the upper 3 levels: L1 {fmt(d.level_values[0])} · L2 {fmt(d.level_values[1])} · L3 {fmt(d.level_values[2])} = {fmt(d.drop_distribution)} per drop.</p>
            <div className="grid grid-cols-3 gap-4">
              {m.level_slots.map((s, i) => (
                <div key={i} className="rounded-2xl p-4 text-center bg-white/[0.03] border border-white/10" data-testid={`mc-level-${i + 1}`}>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Level {i + 1}</div>
                  <div className="font-num text-2xl font-bold text-white mt-1">{m.level_filled[i]}<span className="text-slate-500 text-base">/{s}</span></div>
                  <div className="text-xs text-slate-500 mt-1">boxes filled</div>
                </div>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-6 overflow-x-auto" data-testid="mc-tree">
            {(!m.total_drops) ? (
              <p className="text-sm text-slate-500 py-16 text-center">No matrix boxes filled yet for this member.</p>
            ) : (
              <MatrixTree root={root} width={m.width} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
